// parserHost - the one place a tree-sitter grammar is loaded and a source is
// parsed.
//
// WHY A REAL PARSER. Every shell-reading surface in the product runs on
// quote-aware scanners (`xray-factory/src/mine/shell.js`, the signals catalog's
// `_segments`), and they agree on the traps that cost real debugging while
// still mis-reading the shapes a grammar gets for free: `$(a; b)` splits into
// two statements and puts `b` in command position, `for f in *; do rm $f; done`
// puts `do` there, `--flag="a b"` splits a path in half, and `<(sort a)` is
// shredded into tokens. Those are not tuning gaps; a scanner cannot see nesting.
//
// WHY TREE-SITTER RATHER THAN A LANGUAGE'S OWN PARSER. The input is captured
// text from heterogeneous harnesses, routinely CLIPPED mid-command by the
// capture caps upstream. `ast.parse`, `mvdan/sh` and acorn all fail closed on
// malformed input and return nothing; tree-sitter recovers, yielding the
// commands it could see plus `hasError` marking the tree incomplete. That is
// the whole reason the scanners were coarse in the first place, answered
// structurally instead of by imprecision. It also means ONE mechanism across
// bash, PowerShell, Python and JavaScript rather than four scanners that each
// degrade differently.
//
// WHAT A CLEAN TREE DOES NOT MEAN. Clipping is not always visible in the tree:
// `cat /etc/passwd && rm -rf /var` clipped from `/var/tmp/build` parses with NO
// error and a plausible, WRONG path. So callers must pass `clipped` from the
// capture metadata that knows bytes were dropped, and treat the tail as
// unresolved regardless of `hasError`. A parser cannot recover a byte it was
// never given.
//
// CSP. The prod/staging policy is `script-src 'self' 'wasm-unsafe-eval' ...`
// with no `'unsafe-eval'`. WebAssembly instantiation is permitted; this module
// adds no `eval` or `new Function`. Tree-sitter QUERIES are S-expression DATA
// compiled by the wasm, not code, which is what lets a runtime lens carry one -
// the same reasoning `xrayRuntimeLens.js` already applies to `new RegExp`.
//
// The bundler will still warn about `eval`, and the warning is real but does
// not apply: emscripten's runtime carries `addEmAsm`/`addEmJs`, which `eval`
// the inline JavaScript of EM_ASM/EM_JS sections, and those survive
// minification into the bundle. Those grammars declare no such sections, so
// the paths never run. Verified rather than assumed - the built bundle was
// served under the frontend module's verbatim CSP and parsed bash, PowerShell
// and a query with no violation and no console error. A future wasm dependency
// that DOES use EM_ASM would fail here, and would fail only in a deployed
// build, never on the dev server.
import { Parser, Language } from "web-tree-sitter";

// Static `new URL` literals so Vite can see each grammar and emit it as a build
// asset. A computed path here would produce a URL that resolves in dev and
// 404s in the built bundle.
const GRAMMAR_URLS = {
  bash: new URL("../../assets/tree-sitter/tree-sitter-bash.wasm", import.meta.url),
  powershell: new URL("../../assets/tree-sitter/tree-sitter-powershell.wasm", import.meta.url),
  python: new URL("../../assets/tree-sitter/tree-sitter-python.wasm", import.meta.url),
  javascript: new URL("../../assets/tree-sitter/tree-sitter-javascript.wasm", import.meta.url),
};

export const GRAMMAR_LANGUAGES = Object.freeze(Object.keys(GRAMMAR_URLS));
export const isGrammarLanguage = (language) => Object.hasOwn(GRAMMAR_URLS, String(language));

// Past this, a "command" is a payload that happens to be quoted, and the tree
// costs more than any reading of it is worth. The judge's own source window is
// 6k; code-mode cells are the large end and still land far below this.
export const MAX_SOURCE_CHARS = 262_144;
// Parsing is near-linear, but this runs where a reader is waiting. The deadline
// is wall-clock rather than a node budget because that is what a reader feels.
const PARSE_DEADLINE_MS = 250;
// Trees hold wasm memory until deleted, so the cache is small and evicts
// explicitly. Keyed on (language, source), which is what makes re-opening the
// same trace free.
const TREE_CACHE_MAX = 48;

const languages = new Map();
const loading = new Map();
const parsers = new Map();
const treeCache = new Map();
let initPromise = null;
let unavailable = false;

/** djb2, the sibling libs' stable hash. Keys the tree cache without holding the source. */
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// Whether this is Node rather than a browser. NOT inferred from the asset
// URL's protocol: Vite rewrites `new URL(..., import.meta.url)` into an asset
// reference, so under jsdom it resolves to `http://localhost:3000/...` even
// though the code is running in Node. Reading that as "browser" made every
// grammar fail to load in exactly the component tests that exercise the
// readers, silently, because the load path swallows its own failure.
const IN_NODE = typeof process !== "undefined" && Boolean(process.versions?.node);

/** Read an asset the bundler resolved, from disk, whatever URL shape it took. */
async function readAsset(url) {
  const { readFile } = await import("node:fs/promises");
  const { fileURLToPath } = await import("node:url");
  const { resolve } = await import("node:path");
  if (url.protocol === "file:") return new Uint8Array(await readFile(fileURLToPath(url)));
  // An http asset URL under a test runner: its pathname is the repository path.
  return new Uint8Array(await readFile(resolve(process.cwd(), `.${url.pathname}`)));
}

function grammarSource(language) {
  const url = GRAMMAR_URLS[language];
  // In the browser the URL is fetched; in Node the bytes are read, because
  // `Language.load` would otherwise fetch an origin nothing is serving.
  return IN_NODE ? readAsset(url) : Promise.resolve(url.href);
}

/**
 * Emscripten's own runtime wasm, read from disk when we are not in a browser.
 *
 * Under jsdom the glue sees `window` and takes its WEB path, then fetches
 * `web-tree-sitter.wasm` relative to the document URL - which nothing serves,
 * so every grammar silently failed to load in exactly the component tests that
 * exercise the readers. Handing it the bytes removes the branch: in a real
 * browser Vite resolves the asset and the fetch works, and everywhere else the
 * file is read.
 */
async function runtimeWasm() {
  if (!IN_NODE) return null;
  const { readFile } = await import("node:fs/promises");
  const { resolve } = await import("node:path");
  try {
    return new Uint8Array(await readFile(resolve(process.cwd(), "node_modules/web-tree-sitter/web-tree-sitter.wasm")));
  } catch {
    return null;
  }
}

async function init() {
  if (!initPromise) {
    initPromise = (async () => {
      const wasmBinary = await runtimeWasm();
      return Parser.init(wasmBinary ? { wasmBinary } : undefined);
    })();
  }
  return initPromise;
}

/**
 * The parser for one language, or null when the grammar cannot be loaded.
 *
 * Null rather than a throw: a missing grammar asset must degrade the reading
 * that wanted it, never take down the surface it was called from. The failure
 * is latched so a broken asset costs one fetch, not one per call.
 */
export async function loadParser(language) {
  if (!isGrammarLanguage(language)) return null;
  // The cache wins over the latch: one grammar failing to load must not
  // disable a parser that already loaded and works - and parseSourceSync
  // reads the cache directly, so the two paths would otherwise disagree.
  if (parsers.has(language)) return parsers.get(language);
  if (unavailable) return null;
  if (!loading.has(language)) {
    loading.set(language, (async () => {
      await init();
      const loaded = await Language.load(await grammarSource(language));
      languages.set(language, loaded);
      const parser = new Parser();
      parser.setLanguage(loaded);
      parsers.set(language, parser);
      return parser;
    })().catch((error) => {
      loading.delete(language);
      // One failure here means the wasm is absent or wrong for every caller,
      // not just this one: `npm run assets:grammars` has not run, or the build
      // dropped the asset.
      unavailable = true;
      if (typeof console !== "undefined") {
        console.warn(`tree-sitter grammar unavailable (${language}): ${error?.message || error}`);
      }
      return null;
    }));
  }
  return loading.get(language);
}

/** The loaded `Language`, for constructing a Query against it. */
export async function loadLanguage(language) {
  await loadParser(language);
  return languages.get(language) ?? null;
}

function cachePut(key, entry) {
  treeCache.set(key, entry);
  while (treeCache.size > TREE_CACHE_MAX) {
    const oldest = treeCache.keys().next().value;
    treeCache.get(oldest)?.tree?.delete?.();
    treeCache.delete(oldest);
  }
}

/**
 * Parse one source, or null when the grammar is unavailable, the source is
 * empty, or parsing hit the deadline.
 *
 * @param clipped  whether the CAPTURE dropped bytes off this source. It is not
 *   derivable from the tree - see the header - and callers that have it must
 *   pass it, because a clean tree over a clipped source states a path that was
 *   never run.
 * @returns {{tree, root, language, hasError, clipped, truncated}|null}
 *   `truncated` means THIS module cut the source at `MAX_SOURCE_CHARS`;
 *   `clipped` is the caller's upstream loss. Either one means the tail of the
 *   tree describes less than the agent actually ran.
 */
export async function parseSource(language, text, { clipped = false } = {}) {
  const source = String(text ?? "");
  if (!source.trim()) return null;
  const parser = await loadParser(language);
  if (!parser) return null;

  const truncated = source.length > MAX_SOURCE_CHARS;
  const body = truncated ? source.slice(0, MAX_SOURCE_CHARS) : source;
  const key = `${language}:${body.length}:${hashString(body)}`;
  const hit = treeCache.get(key);
  if (hit) {
    // Refresh recency; a trace re-read walks the same calls in the same order.
    treeCache.delete(key);
    treeCache.set(key, hit);
    return { ...hit, clipped: clipped || hit.clipped };
  }

  const deadline = Date.now() + PARSE_DEADLINE_MS;
  let tree = null;
  try {
    tree = parser.parse(body, null, { progressCallback: () => Date.now() > deadline });
  } catch (error) {
    if (typeof console !== "undefined") {
      console.warn(`tree-sitter parse failed (${language}): ${error?.message || error}`);
    }
    return null;
  }
  // A cancelled parse returns null rather than a partial tree. Callers see the
  // same "no reading" answer as a missing grammar, which is the honest one.
  if (!tree) return null;

  const entry = {
    tree,
    root: tree.rootNode,
    language,
    hasError: Boolean(tree.rootNode?.hasError),
    truncated,
    clipped,
  };
  cachePut(key, entry);
  return entry;
}

/**
 * Parse WITHOUT awaiting, or null if the grammar is not loaded yet.
 *
 * Parsing itself is synchronous - only `Parser.init` and `Language.load` are
 * not - and that matters because the code-mode unwrap runs inside a `useMemo`
 * before the semantic feed is built. That pass cannot become async without
 * making the whole model async, so it asks for a parse it can use right now
 * and gets a straight answer when the answer is no.
 *
 * Callers pair this with `warmGrammar` and re-run when it resolves.
 */
export function parseSourceSync(language, text, { clipped = false } = {}) {
  const source = String(text ?? "");
  if (!source.trim()) return null;
  const parser = parsers.get(language);
  if (!parser) return null;

  const truncated = source.length > MAX_SOURCE_CHARS;
  const body = truncated ? source.slice(0, MAX_SOURCE_CHARS) : source;
  const key = `${language}:${body.length}:${hashString(body)}`;
  const hit = treeCache.get(key);
  if (hit) {
    treeCache.delete(key);
    treeCache.set(key, hit);
    return { ...hit, clipped: clipped || hit.clipped };
  }
  const deadline = Date.now() + PARSE_DEADLINE_MS;
  let tree = null;
  try {
    tree = parser.parse(body, null, { progressCallback: () => Date.now() > deadline });
  } catch {
    return null;
  }
  if (!tree) return null;
  const entry = {
    tree, root: tree.rootNode, language,
    hasError: Boolean(tree.rootNode?.hasError), truncated, clipped,
  };
  cachePut(key, entry);
  return entry;
}

/** Whether `parseSourceSync` can answer for this language right now. */
export const grammarReady = (language) => parsers.has(language);

/**
 * Load a grammar so the synchronous path can be used, and report when it is
 * ready. Safe to call repeatedly; the load is shared.
 */
export async function warmGrammar(language) {
  return Boolean(await loadParser(language));
}

/**
 * Whether a parse can be trusted to describe everything the agent ran.
 *
 * The three ways it cannot are deliberately one predicate: a caller that
 * remembers `hasError` but forgets `clipped` writes exactly the bug the header
 * warns about.
 */
export const parseIsComplete = (parsed) =>
  Boolean(parsed) && !parsed.hasError && !parsed.truncated && !parsed.clipped;

/** Drop every cached tree and its wasm memory. Parsers and grammars are kept. */
export function releaseTrees() {
  for (const entry of treeCache.values()) entry.tree?.delete?.();
  treeCache.clear();
}

/** What the host is holding. For tests and for reasoning about a wedged panel. */
export function parserHostState() {
  return {
    languages: [...parsers.keys()],
    cached: treeCache.size,
    unavailable,
  };
}

/** Every suite shares this module; a latched failure would poison the next one. */
export function _resetParserHostForTests() {
  releaseTrees();
  unavailable = false;
}

// Test-only: the latch is set by a real load failure, which a unit test
// cannot arrange without breaking an asset. Setting it directly is what lets
// the guard ORDER be pinned - a cached parser must survive the latch.
export function _setUnavailableForTests(value) {
  unavailable = Boolean(value);
}
